import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// components and helpers
import { workspaceActions } from '../WorkspaceActions';
import { showNotification } from '../../Notifications/NotificationActions';
import * as confirmActions from '../../Confirmation/ConfirmationActions';
import * as dashboardActions from '../Dashboard/DashboardActions';
import { caseQueries, fileQueries } from './helpers/queries';

// mui
import Button from '@mui/material/Button';
import StarRateIcon from '@mui/icons-material/StarRate';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import BoltIcon from '@mui/icons-material/Bolt';
import BrightnessLowIcon from '@mui/icons-material/BrightnessLow';
import CloudIcon from '@mui/icons-material/Cloud';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Paper, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import CancelIcon from '@mui/icons-material/Cancel';
import FolderIcon from '@mui/icons-material/Folder';

const useStyles = makeStyles({
    root: {
        '& .MuiInput-underline:before': {
            borderBottom: 'none', // Remove the underline
        },
        '& .MuiInput-underline:after': {
            borderBottom: 'none', // Remove the underline
        },
    },
});

const Profile: React.FC = () => {
    // Basic setup
    const theme = useTheme();
    const dispatch = useDispatch();
    const classes = useStyles();
    let { id } = useParams();

    // State, useState setup
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(false);
    const [accountInfo, setAccountInfo] = useState({
        description: '',
        icon: '',
        name: '',
    });
    const [originalAccountInfo, setOriginalAccountInfo] = useState({
        ...accountInfo,
    });
    const dashboard = useSelector((state: any) => state.dashboard);

    // fields stuff, this had to go after error setup

    const fields = [
        {
            label: 'Name',
            name: 'name',
            placeholder: 'John Doe',
            error: error,
            helperText: error ? 'Invalid name' : null,
        },
        {
            label: 'Description',
            name: 'description',
            placeholder: "This is john doe's workspace",
        },
    ];

    const iconList = [
        {
            key: 'star',
            value: <StarRateIcon />,
        },
        {
            key: 'heart',
            value: <FavoriteIcon />,
        },
        {
            key: 'autoGraph',
            value: <AutoGraphIcon />,
        },
        {
            key: 'moon',
            value: <BedtimeIcon />,
        },
        {
            key: 'lightning',
            value: <BoltIcon />,
        },
        {
            key: 'sun',
            value: <BrightnessLowIcon />,
        },
        {
            key: 'cloud',
            value: <CloudIcon />,
        },
        {
            key: 'flower',
            value: <FilterVintageIcon />,
        },
        {
            key: 'folder',
            value: <FolderIcon />,
        },
    ];

    // useSelector setup
    const currentUser = useSelector(
        (state: any) => state.workspace.currentWorkspace
    );
    const currentUserLoading = useSelector(
        (state: any) => state.workspace.currentWorkspaceLoading
    );

    useEffect(() => {
        if (!currentUserLoading && !accountInfo.name) {
            setAccountInfo(currentUser);
        }
    }, [accountInfo.name, currentUser, currentUserLoading]);

    useEffect(() => {
        dispatch(
            dashboardActions?.getDashboardDataMultiple('CASE', id, caseQueries)
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple('FILE', id, fileQueries)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Next up is the handle change functions

    const handleInputChange = (
        event: React.ChangeEvent<{ name?: string; value: unknown }>
    ) => {
        const name = event.target.name ? event.target.name : '';
        const value = event.target.name ? String(event.target.value) : '';

        if (name === 'icon') {
            setAccountInfo({
                ...accountInfo,
                icon: value,
            });
        } else {
            setAccountInfo({
                ...accountInfo,
                [name]: value,
            });
        }
        setError(name === 'name' && value.length === 0);
    };

    // Last but not least is the handle button click functions

    const handleEditClick = () => {
        setOriginalAccountInfo({ ...accountInfo });
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setAccountInfo({ ...originalAccountInfo });
        setIsEditing(false);
        setError(false);
    };

    const handleLockClick = () => {
        if (!error) {
            setIsEditing(false);
            dispatch(workspaceActions?.updateWorkspace(id, accountInfo));
            dispatch(
                showNotification(
                    'Your information was successfully updated.',
                    'success'
                )
            );
        } else {
            dispatch(
                showNotification(
                    'The information you entered was invalid. Please try again',
                    'error'
                )
            );
        }
    };

    const handleDeleteWorkspace = () => {
        if (
            (dashboard &&
                dashboard.caseList &&
                dashboard.caseList.casesReportCounts &&
                dashboard.caseList.casesReportCounts.items &&
                dashboard.caseList.casesReportCounts.items[0] &&
                dashboard.caseList.casesReportCounts.items[0].COUNT &&
                dashboard.caseList.casesReportCounts.items[0].COUNT > 0) ||
            (dashboard &&
                dashboard.fileCount &&
                dashboard.fileCount.importReportCounts &&
                dashboard.fileCount.importReportCounts.items &&
                dashboard.fileCount.importReportCounts.items[0] &&
                dashboard.fileCount.importReportCounts.items[0].COUNT &&
                dashboard.fileCount.importReportCounts.items[0].COUNT > 0)
        ) {
            dispatch(
                confirmActions.showConfirmation(
                    'Delete Workspace - Data Found',
                    'Are you sure you want to delete this workspace and all the associated data? This action is not reversible and all data will be lost. Please contact our support team to assist.',
                    'unableDeleteWorkspace',
                    id
                )
            );
        } else {
            dispatch(
                confirmActions.showConfirmation(
                    'Delete Workspace',
                    'Are you sure you want to delete this workspace?',
                    'deleteWorkspace',
                    id
                )
            );
        }
    };

    // The actual important stuff that does the thing

    return (
        <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
                <Paper elevation={0} style={{ padding: 10, borderRadius: 4 }}>
                    <Box
                        sx={{
                            padding: '10px',
                            maxWidth: '65%',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="h6"
                                align="left"
                                fontWeight={'bold'}
                            >
                                My Workspace Details
                            </Typography>
                            {isEditing ? (
                                <>
                                    <IconButton onClick={handleCancelClick}>
                                        <CancelIcon />
                                    </IconButton>
                                    <IconButton onClick={handleLockClick}>
                                        <SaveIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <IconButton onClick={handleEditClick}>
                                    <EditIcon />
                                </IconButton>
                            )}
                        </div>

                        <hr
                            style={{
                                borderTop: '2px solid',
                                borderColor: theme.palette.primary.main,
                                width: '100%',
                                marginTop: '10px',
                                marginBottom: '20px',
                            }}
                        />
                        <>
                            {fields.map((field) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <TextField
                                        id="outlined-basic"
                                        label={field.label}
                                        name={field.name}
                                        variant={
                                            isEditing ? 'outlined' : 'standard'
                                        }
                                        style={{ width: '100%' }}
                                        placeholder={field.placeholder}
                                        value={
                                            isEditing
                                                ? accountInfo[field.name] || ''
                                                : accountInfo[field.name] ||
                                                  '---'
                                        }
                                        onChange={handleInputChange}
                                        size={'small'}
                                        disabled={!isEditing}
                                        className={
                                            !isEditing ? classes.root : ''
                                        }
                                        error={field.error}
                                        helperText={field.helperText}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled':
                                                {
                                                    WebkitTextFillColor:
                                                        theme.palette.primary
                                                            .main,
                                                },
                                        }}
                                    />
                                </Box>
                            ))}
                        </>
                        <FormControl fullWidth sx={{ marginTop: 1 }}>
                            <InputLabel id="demo-simple-select-label"></InputLabel>
                            <TextField
                                variant={isEditing ? 'outlined' : 'standard'}
                                disabled={!isEditing}
                                className={!isEditing ? classes.root : ''}
                                sx={{
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        WebkitTextFillColor:
                                            theme.palette.primary.main,
                                    },
                                    '& .MuiSelect-icon': {
                                        display: 'none',
                                    },
                                }}
                                name={'icon'}
                                label="Icon"
                                select
                                value={accountInfo.icon || 'folder'}
                                onChange={handleInputChange}
                            >
                                {iconList.map((icon) => (
                                    <MenuItem key={icon.key} value={icon.key}>
                                        <ListItemIcon>
                                            {icon.value}
                                        </ListItemIcon>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormControl>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={0} style={{ padding: 10, borderRadius: 4 }}>
                    <Box
                        sx={{
                            padding: '10px',
                            maxWidth: '65%',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="h6"
                                align="left"
                                fontWeight={'bold'}
                            >
                                Danger Zone
                            </Typography>
                        </div>
                        <hr
                            style={{
                                borderTop: '2px solid',
                                borderColor: theme.palette.primary.main,
                                width: '100%',
                                marginTop: '10px',
                                marginBottom: '20px',
                            }}
                        />
                        <Grid
                            container
                            justifyContent={'space-between'}
                            style={{
                                backgroundColor: '#FF454730',
                                padding: 20,
                                borderRadius: 4,
                            }}
                            alignItems={'center'}
                        >
                            <Grid item>
                                <Typography
                                    variant={'body1'}
                                    sx={{
                                        fontWeight: 'bold',
                                        color:
                                            theme.palette.mode === 'light'
                                                ? '#8B0000'
                                                : '#fff',
                                    }}
                                >
                                    Delete this workspace
                                </Typography>
                                <Typography
                                    variant={'body2'}
                                    sx={{
                                        color:
                                            theme.palette.mode === 'light'
                                                ? '#8B0000'
                                                : '#fff',
                                    }}
                                >
                                    Once you delete a workspace, all data
                                    associated will be lost.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant={'contained'}
                                    color={'error'}
                                    sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: '#B30000',
                                    }}
                                    onClick={handleDeleteWorkspace}
                                >
                                    Delete
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Profile;
